// src/utils/priceUtils.ts

/**
 * Safely round to 2 decimals (for calculations & display precision)
 */
const round2 = (value: number): number => {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
};

/**
 * Round payable amount to nearest rupee
 * (single source of truth for checkout & payment)
 */
const roundRupee = (value: number): number => {
  return Math.round(Number(value));
};

export interface ShippingConfig {
  enabled: boolean;
  freeThreshold: number;
  flatCharge: number;
  gstRate: number;
}

export interface CartItem {
  variantId: number;
  productName: string;
  salePrice: number;
  originalPrice?: number;
  offerPrice?: number;
  quantity: number;
}

export interface PriceSummary {
  totalMrp: number;
  totalSelling: number;
  totalDiscount: number;
  shippingCharge: number;
  shippingGstAmount: number;
  grandTotal: number;
  rawGrandTotal: number;
  isEstimated: boolean;
}

/**
 * Calculate price summary for UI display ONLY
 * Backend remains the final source of truth
 */
export const calculatePriceSummary = ({
  items = [],
  shippingConfig = null,
}: {
  items: CartItem[];
  shippingConfig: ShippingConfig | null;
}): PriceSummary => {
  let totalMrp = 0;       // Total MRP
  let totalSelling = 0;  // Total selling price (offer/sale)

  // 1️⃣ Calculate item totals
  items.forEach((item) => {
    const mrp = Number(item.originalPrice ?? item.salePrice ?? 0);
    const selling = Number(item.offerPrice ?? item.salePrice ?? 0);
    const qty = Number(item.quantity ?? 0);

    totalMrp += mrp * qty;
    totalSelling += selling * qty;
  });

  totalMrp = round2(totalMrp);
  totalSelling = round2(totalSelling);

  const totalDiscount = round2(totalMrp - totalSelling);

  // 2️⃣ Shipping (UI estimate only)
  let shippingCharge = 0;
  let shippingGstAmount = 0;

  if (shippingConfig?.enabled) {
    const freeThreshold = Number(shippingConfig.freeThreshold ?? 0);
    const flatCharge = Number(shippingConfig.flatCharge ?? 0);
    const gstRate = Number(shippingConfig.gstRate ?? 0);

    if (totalSelling < freeThreshold && flatCharge > 0) {
      shippingCharge = round2(flatCharge);

      if (gstRate > 0) {
        shippingGstAmount = round2((shippingCharge * gstRate) / 100);
      }
    }
  }

  // 3️⃣ Final payable amount
  const rawGrandTotal = round2(
    totalSelling + shippingCharge + shippingGstAmount
  );

  // ✅ SINGLE rounding point (rupees)
  const grandTotal = roundRupee(rawGrandTotal);

  return {
    // Prices
    totalMrp,
    totalSelling,
    totalDiscount,

    // Shipping
    shippingCharge,
    shippingGstAmount,

    // Final
    grandTotal,        // ✅ integer rupees (use everywhere)
    rawGrandTotal,     // optional: debugging / audit

    // UI flag
    isEstimated: true,
  };
};

/**
 * Optional helper for UI formatting only (for labels, not totals)
 */
export const formatPrice = (value: number): string => {
  return `₹${round2(value).toFixed(2)}`;
};